import { Link } from "react-router-dom";
import { ArrowUpRight, Check } from "lucide-react";
import { Reveal } from "@/components/Reveal";
import { SERVICES } from "@/data/content";

const PageHeader = ({ overline, title, subtitle }) => (
  <section className="relative overflow-hidden pt-40 pb-16">
    <div className="pointer-events-none absolute -right-32 top-20 h-96 w-96 rounded-full bg-brand/10 blur-3xl" />
    <div className="mx-auto max-w-7xl px-6 lg:px-10">
      <Reveal><span className="text-xs font-bold uppercase tracking-[0.2em] text-brand">{overline}</span></Reveal>
      <Reveal delay={0.1}><h1 className="mt-5 max-w-4xl font-display text-5xl font-black leading-[0.95] tracking-tighter text-slate-900 sm:text-6xl lg:text-7xl">{title}</h1></Reveal>
      {subtitle && <Reveal delay={0.2}><p className="mt-6 max-w-2xl text-lg leading-relaxed text-slate-600">{subtitle}</p></Reveal>}
    </div>
  </section>
);

export default function Services() {
  return (
    <div data-testid="services-page">
      <PageHeader
        overline="Services"
        title="Everything you need to grow — under one roof."
        subtitle="Six specialist services that work together as one growth machine. Pick a single channel or let us run the whole playbook."
      />
      <section className="relative z-10 pb-28">
        <div className="mx-auto max-w-7xl space-y-6 px-6 lg:px-10">
          {SERVICES.map((s, i) => (
            <Reveal key={s.slug} delay={0.05}>
              <div data-testid={`service-row-${s.slug}`} className="grid overflow-hidden rounded-3xl border border-slate-200 bg-white lg:grid-cols-2">
                <div className={`p-10 lg:p-14 ${i % 2 ? "lg:order-2" : ""}`}>
                  <div className="flex items-center gap-4">
                    <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-brand/10 text-brand"><s.icon className="h-6 w-6" /></div>
                    <span className="font-display text-4xl font-black text-slate-100">{s.n}</span>
                  </div>
                  <h2 className="mt-6 font-display text-3xl font-extrabold tracking-tight text-slate-900">{s.title}</h2>
                  <p className="mt-4 text-base leading-relaxed text-slate-600">{s.short}</p>
                  <ul className="mt-6 space-y-3">
                    {s.points.map((p) => (
                      <li key={p} className="flex items-center gap-3 text-sm font-medium text-slate-700">
                        <span className="flex h-5 w-5 items-center justify-center rounded-full bg-brand text-white"><Check className="h-3 w-3" /></span>{p}
                      </li>
                    ))}
                  </ul>
                  <Link to="/contact" className="mt-8 inline-flex items-center gap-2 text-sm font-semibold text-brand">Get started <ArrowUpRight className="h-4 w-4" /></Link>
                </div>
                <div className={`relative min-h-[280px] overflow-hidden bg-slate-100 ${i % 2 ? "lg:order-1" : ""}`}>
                  <img src={s.image} alt={s.title} className="h-full w-full object-cover" />
                  <div className="absolute inset-0 bg-gradient-to-t from-brand-navy/40 to-transparent" />
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </section>
      <section className="relative z-10 pb-28">
        <div className="mx-auto max-w-7xl px-6 lg:px-10">
          <div className="rounded-3xl bg-brand-navy px-8 py-14 text-center text-white lg:px-20">
            <h2 className="font-display text-3xl font-black tracking-tighter sm:text-5xl">Not sure where to start?</h2>
            <p className="mx-auto mt-4 max-w-xl text-white/70">Book a free strategy call and we'll recommend the exact services that will move the needle fastest for your business.</p>
            <Link to="/contact" data-testid="services-cta-button" className="mt-8 inline-flex items-center gap-2 rounded-full bg-brand px-7 py-4 text-sm font-bold text-white transition-colors hover:bg-brand-hover">Book a free call <ArrowUpRight className="h-4 w-4" /></Link>
          </div>
        </div>
      </section>
    </div>
  );
}
